import { useState, useEffect, useCallback } from "react";
import { useAuthToken } from "@/contexts/AuthContext";

export interface MyInviteCode {
  code: string;
  share_url: string;
  is_active: boolean;
  referee_bonus_credits: number;
  referrer_bonus_credits: number;
  free_plan_credits: number;
}

export interface MyInviteStats {
  total_invited: number;
  total_converted: number;
  total_credits_earned: number;
}

const API_BASE = import.meta.env.VITE_BASE_API_URL;

export function buildShareMessage(refereeTotal: number): string {
  return refereeTotal > 0
    ? `Join me on Joylo! where ideas become reality! Get ${refereeTotal} credits when you sign up and start building apps with AI.`
    : `Join me on Joylo! where ideas become reality! Start building fully functional apps with AI.`;
}

// Shared by the Invite Friends card and the Referrals page: the user's invite code plus their referral stats
export function useMyInvite() {
  const token = useAuthToken();
  const [invite, setInvite] = useState<MyInviteCode | null>(null);
  const [stats, setStats] = useState<MyInviteStats | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [codeRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/invites/my-code`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE}/invites/my-stats`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (codeRes.ok) setInvite(await codeRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch {
      // Non-critical widget — fail silently, section just stays empty.
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  return { invite, stats, loading };
}
